import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { useQueueByTable } from './useQueueByTable'
import { enqueueMutation } from '../lib/mutationQueue'
import { getCached, setCached } from '../lib/hookCache'

const TABLE = 'user_profiles'
const CACHE_SCOPE = 'user_profile'

export function useProfile() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [serverProfile, setServerProfile] = useState(cached || null)
  const [loading, setLoading] = useState(cached === undefined)
  const [error, setError] = useState(null)
  const pending = useQueueByTable(TABLE)
  const initialLoadedRef = useRef(cached !== undefined)

  const fetchProfile = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()

    if (err) {
      setError(err.message)
    } else {
      const next = data || null
      setServerProfile(next)
      setCached(CACHE_SCOPE, user.id, next)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  usePhoenixRefresh(fetchProfile)

  // Merge: profile is a single row per user. Apply pending upserts on top.
  const profile = useMemo(() => {
    let merged = serverProfile ? { ...serverProfile } : null
    for (const m of pending) {
      if (m.op === 'upsert') {
        merged = { ...(merged || {}), ...m.payload, _pending: m }
      }
    }
    return merged
  }, [serverProfile, pending])

  const upsertHeight = async (heightCm) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, height_cm: heightCm, updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  const upsertAvatar = async (avatarUrl) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, avatar_url: avatarUrl, updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  const markWelcomeSeen = async () => {
    if (!user) return false
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, welcome_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  const markTourSeen = async () => {
    if (!user) return false
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, tour_seen_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  // Resets tour_seen_at so the user can replay the spotlight from
  // Settings. The welcome carousel intentionally does NOT re-fire on
  // replay — it's a one-time onboarding intro, not a recurring tour.
  const resetTour = async () => {
    if (!user) return false
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, tour_seen_at: null, updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  // Adds a feature key to features_seen so the pulse badge for that
  // feature fades out and doesn't reappear. Appends, not replaces —
  // we read the current set off `profile` then push.
  const acknowledgeFeature = async (featureKey) => {
    if (!user || !featureKey) return false
    const current = profile?.features_seen || []
    if (current.includes(featureKey)) return true
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: {
        user_id: user.id,
        features_seen: [...current, featureKey],
        updated_at: new Date().toISOString(),
      },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  // Generic partial-upsert for the standalone profile-edit page.
  // Accepts any subset of identity fields; null/'' becomes a DB null
  // so the form's "Clear" button works cleanly.
  const upsertProfile = async (patch) => {
    if (!user) return false
    setError(null)
    const cleaned = {}
    for (const [k, v] of Object.entries(patch || {})) {
      cleaned[k] = v === '' ? null : v
    }
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, ...cleaned, updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  const upsertWaterGoal = async (waterGoalMl) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { user_id: user.id, water_goal_ml: waterGoalMl, updated_at: new Date().toISOString() },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  // Daily macro targets — all four are optional. Pass null/'' to clear a
  // field; the helper coerces empty strings to null so the row clears
  // cleanly when the user taps "Clear all".
  const upsertMacroTargets = async ({ kcal, protein, carbs, fat }) => {
    if (!user) return false
    setError(null)
    const toIntOrNull = (v) => {
      if (v === '' || v == null) return null
      const n = Number(v)
      return Number.isFinite(n) && n >= 0 ? Math.round(n) : null
    }
    const toNumOrNull = (v) => {
      if (v === '' || v == null) return null
      const n = Number(v)
      return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : null
    }
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: {
        user_id: user.id,
        kcal_target: toIntOrNull(kcal),
        protein_target_g: toNumOrNull(protein),
        carbs_target_g: toNumOrNull(carbs),
        fat_target_g: toNumOrNull(fat),
        updated_at: new Date().toISOString(),
      },
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  // Persists the full goal payload from the onboarding wizard. Any null/undefined
  // fields on the input are simply omitted from the upsert. Always sets
  // onboarded_at so the wizard won't re-appear.
  const completeOnboarding = async (values) => {
    if (!user) return false
    setError(null)
    const payload = {
      user_id: user.id,
      onboarded_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    if (values.startWeight != null) payload.start_weight = values.startWeight
    if (values.startDate) payload.start_date = values.startDate
    if (values.goalWeight != null) payload.goal_weight = values.goalWeight
    if (values.goalDate) payload.goal_date = values.goalDate
    if (values.heightCm != null) payload.height_cm = values.heightCm
    if (values.waterGoalMl != null) payload.water_goal_ml = values.waterGoalMl
    if (values.name != null) payload.name = String(values.name).trim().slice(0, 60)
    if (values.birthday) payload.birthday = values.birthday
    if (values.sex) payload.sex = values.sex

    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload,
      conflictKey: 'user_id',
      userId: user.id,
    })
    return true
  }

  return {
    profile,
    loading,
    error,
    upsertHeight,
    upsertAvatar,
    upsertProfile,
    markWelcomeSeen,
    markTourSeen,
    resetTour,
    acknowledgeFeature,
    upsertWaterGoal,
    upsertMacroTargets,
    completeOnboarding,
    refetch: fetchProfile,
  }
}
